import { useState } from "react";
import { AdminActivity, Webinar } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { useRepositoryData } from "../../webinars/useRepositoryData";
import { ActivityGroup, activityGroup, describeActivity, needsReview } from "../../webinars/activity";
import { formatDateTime } from "../../webinars/format";

// The audit log (production only): webinar and joining-link changes, attendance updates, email
// sends and retries, sign-ins, and requests that were blocked or refused. Folded away unless
// something was blocked or refused in the last 24 hours, which it flags for review.

const PAGE = 100;
const DAY_MS = 864e5;

const TABS: { key: "all" | ActivityGroup; label: string }[] = [
  { key: "all", label: "All" },
  { key: "changes", label: "Changes" },
  { key: "emails", label: "Emails" },
  { key: "security", label: "Sign-ins & security" },
];

const OUTCOME_LABEL: Record<AdminActivity["outcome"], string> = { ok: "", blocked: "Blocked", denied: "Refused" };

export default function AdminActivityPanel({ webinarsById }: { webinarsById: Record<string, Webinar> }) {
  const [limit, setLimit] = useState(PAGE);
  const [tab, setTab] = useState<"all" | ActivityGroup>("all");
  /** null until the admin opens or closes it: until then it follows whether anything needs review */
  const [open, setOpen] = useState<boolean | null>(null);
  const { data, loading, error } = useRepositoryData(() => webinarRepository.listAdminActivity(limit), `admin-activity|${limit}`);

  // The prototype keeps no audit log
  if (data === null) return null;

  const entries = data ?? [];
  const recentReview = entries.filter(e => needsReview(e) && Date.now() - Date.parse(e.occurredAt) < DAY_MS).length;
  const expanded = open ?? recentReview > 0;
  const shown = tab === "all" ? entries : entries.filter(e => activityGroup(e) === tab);

  return (
    <section className={`wa-panel wa-activity${recentReview ? " wa-activity--review" : ""}`} id="activity" aria-labelledby="wa-activity-title">
      <div className="wa-panel-head">
        <div>
          <h2 id="wa-activity-title">Activity log</h2>
          <p className="wa-muted wa-panel-sub">Who changed what, emails sent, sign-ins and blocked requests. Kept for 180 days.</p>
        </div>
        <span className={`wa-monitor-status wa-monitor-status--${recentReview ? "warning" : error ? "none" : "ok"}`}>
          {error ? "Not loaded" : recentReview ? `${recentReview} blocked or refused in 24 hours` : "Nothing to review"}
        </span>
      </div>

      <button type="button" className="wa-link wa-monitor-toggle" aria-expanded={expanded} aria-controls="wa-activity-list" onClick={() => setOpen(!expanded)}>
        {expanded ? "Hide activity" : "Show activity"}<span aria-hidden="true">{expanded ? " ▴" : " ▾"}</span>
      </button>

      {expanded && (
        <div id="wa-activity-list" className="wa-activity-body">
          {error ? (
            <p className="wa-form-error" role="alert">Couldn't load the activity log. Reload the page to try again.</p>
          ) : loading && !data ? (
            <p className="wa-empty" role="status">Loading…</p>
          ) : (
            <>
              <div className="wa-status-tabs" role="group" aria-label="Show activity">
                {TABS.map(t => (
                  <button key={t.key} type="button" aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                    {t.label}
                    <span className="wa-tab-count">{t.key === "all" ? entries.length : entries.filter(e => activityGroup(e) === t.key).length}</span>
                  </button>
                ))}
              </div>
              {shown.length === 0 ? (
                <p className="wa-empty">Nothing here yet.</p>
              ) : (
                <ul className="wa-activity-list">
                  {shown.map(entry => (
                    <li key={entry.id} className={`wa-activity-item${needsReview(entry) ? " wa-activity-item--review" : ""}`}>
                      <time dateTime={entry.occurredAt}>{formatDateTime(entry.occurredAt)}</time>
                      <span className="wa-activity-text">
                        {needsReview(entry) && <span className="wa-chip wa-chip--cancelled">{OUTCOME_LABEL[entry.outcome]}</span>}
                        {describeActivity(entry, entry.webinarId ? webinarsById[entry.webinarId]?.title : undefined)}
                      </span>
                      <span className="wa-activity-who">{entry.actorEmail ?? (entry.outcome === "ok" ? "System" : "Visitor")}</span>
                    </li>
                  ))}
                </ul>
              )}
              {entries.length === limit && (
                <button type="button" className="wa-btn wa-btn--quiet" onClick={() => setLimit(l => l + PAGE)}>Show {PAGE} more</button>
              )}
              <p className="wa-muted wa-activity-note">
                Times are SAST. Joining links, passwords and visitors' IP addresses are never recorded. Failed password attempts
                aren't listed here: Supabase limits them, and its Auth logs show them for a day.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
